// Tables/cells · MultiRowStack
//
// Generic vertical stack of compact "card-row" cells — each row shows a
// primary line, optional secondary line, and a tone-colored left border.
// Overflow rows collapse into a `+N more` row at the bottom.
//
// Replaces every entity-specific "stack one item per related row" cell —
// e.g. `TransactionCarrierCell` (one carrier per transport leg),
// `TransactionDriverCell`, `TransactionVehicleCell`, `TransactionTransportPriceCell`.
// The schema's `data` callback projects the related rows into
// `{ id, primary, secondary?, tone? }` so this cell stays domain-free.

import type { ReactNode } from "react";
import { Box } from "@ds/layout/Box";
import { Stack } from "@ds/layout/Stack";
import { TextValue } from "@ds/display/TextValue";

// ─── Inputs (data props — the model) ─────────────────────────────────────

/** Tone vocabulary for the per-row left-border accent.
 *  Maps directly onto Box's `tone` ramp + `divider="left"`. */
export type MultiRowTone = "neutral" | "green" | "red" | "blue" | "amber";

export interface MultiRowEntry {
  /** Stable id passed to onClick. */
  id: string;
  /** Primary line (typically the entity name, big number, label). */
  primary: ReactNode;
  /** Optional secondary line (sub-label, meta, phone, suffix). */
  secondary?: ReactNode;
  /** Tone — drives the left-border color. Default `"neutral"`. */
  tone?: MultiRowTone;
}

export interface MultiRowStackInput {
  /** Rows to render. Empty array renders nothing of substance. */
  rows: ReadonlyArray<MultiRowEntry>;
  /** Maximum rows before `+N more` overflow row kicks in. Default `Infinity`. */
  max?: number;
  /** `data-testid` on the root container. */
  testId?: string;
}

// ─── Outputs (events emitted — controller hooks) ─────────────────────────

export interface MultiRowStackOutput {
  /** Called with the row id when a row is clicked. */
  onClick?: (rowId: string) => void;
}

export type MultiRowStackProps = MultiRowStackInput & MultiRowStackOutput;

// ─── Implementation ──────────────────────────────────────────────────────

export function MultiRowStack({
  rows,
  max = Infinity,
  testId,
  onClick,
}: MultiRowStackProps) {
  const visible = rows.slice(0, max);
  const overflow = rows.length - visible.length;

  return (
    <Stack gap="xs" testId={testId}>
      {visible.map((row) => {
        const handleClick = onClick ? () => onClick(row.id) : undefined;
        return (
          <Box
            key={row.id}
            paddingX="sm"
            paddingY="xs"
            shape="rounded"
            tone={row.tone ?? "neutral"}
            divider="left"
            onClick={handleClick}
            testId={`multi-row-${row.id}`}
          >
            <Stack gap="xs">
              <TextValue>{row.primary}</TextValue>
              {row.secondary != null && (
                <TextValue tone="muted">{row.secondary}</TextValue>
              )}
            </Stack>
          </Box>
        );
      })}

      {overflow > 0 && (
        <Box paddingX="sm" paddingY="xs" testId="multi-row-overflow">
          <TextValue italic tone="muted">{`+${overflow}`}</TextValue>
        </Box>
      )}
    </Stack>
  );
}
